import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { SessionService } from '@app/shared/services/session.service';
import { firstValueFrom } from 'rxjs';
import { ProductsService, ProductTypesService, CollectionResponse, CollectionsService, CategoryResponse, CategoriesService, ProductRequest, ProductTypeResponse, ProductResponse, ProductAttributeDefinitionResponse, FilterRequest, ProductAttributeDefinitionsService, AttributeDataType, ProductAttributeDefinitionTranslationResponse, ProductAttributeValueTranslationResponse, FilterRequestForCombo, BrandsService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Features } from '@src/app/shared/backend/auth/Feature';

@Component({
    selector: 'app-edit-product',
    templateUrl: './edit-product.component.html',
    styleUrl: './edit-product.component.scss',
    host: {
        class: 'app-edit-product'
    },
    standalone: false
})
export class EditProductComponent extends BaseViewComponent implements OnInit {

  brandsList: any[] = [];
  brandsTotalRecords: number = 0;
  brandsRowsPerPageOption: number = 10;
  hasOnlyOneActiveBrand: boolean = false;

  productId: number = 0;
  filterOperator = FilterOperator;

  product: ProductResponse = {};
  productTypes: ProductTypeResponse[] = [];
  productTypeTotalRecords: number = 0;
  productTypeRowsPerPageOption: number = 10;

  canViewPrice: boolean = false;
  collectionsList: CollectionResponse[] = [];
  collectionsTotalRecords: number = 0;
  collectionsRowsPerPageOption: number = 10;

  categoriesList: CategoryResponse[] = [];
  categoriesTotalRecords: number = 0;
  categoriesRowsPerPageOption: number = 10;

  hasLimitedEditionEnabled: boolean = false;
  hasCustomisation: boolean = false;
  hasItemsWithoutSerialNumberEnabled: boolean = false;

  productAttributesFeatureEnabled: boolean = false;
  productAttributeDefinitions: ProductAttributeDefinitionResponse[] = [];

  public productForm: FormGroup = this.fb.group({
    brandId: [[]],
    name: ['', [Validators.required, Validators.maxLength(50)]],
    sku: ['', [Validators.required]],
    productTypeCode: [null],
    productType: [[], [Validators.required]],
    collections: [[]],
    categories: [[]],
    price: [null],
    description: ['', [Validators.required, Validators.maxLength(100)]],
    warrantyDurationMonths: ['', [Validators.required]],
    imageUrl: [''],
    comment: [''],
    isSerializable: ['false'],
    isCustomisable: ['false'],
    isWarrantyCardMandatory: ['false'],
    isSerialNumberRequired: ['false'],
    isLimitedEdition: ['false'],
    limitedEditionSize: [null],
    generateLimitedEditionSerialNumbers: [false],
    productAttributes: this.fb.array([])
  });

  constructor(
    public session: SessionService,
    private route: ActivatedRoute,
    private router: Router,
    private productsService: ProductsService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private productTypesService: ProductTypesService,
    private collectionsService: CollectionsService,
    private categoriesService: CategoriesService,
    private permissionService: PermissionService,
    private brandsService: BrandsService,
    private fb: FormBuilder
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  get productAttributesArray(): FormArray {
    return this.productForm.get('productAttributes') as FormArray;
  }

  private async init(): Promise<void> {
    const settings = await this.session.getCompanySettings();


    this.productAttributesFeatureEnabled = settings.features?.some(feature => feature.name === 'ProductAttributes' && feature.isEnabled) ?? false;

    this.route.params.subscribe(async params => {
      this.productId = params['productId'];

      if (this.productId) {
        this.product = await firstValueFrom(this.productsService.apiProductsIdGet(this.productId));

        await this.loadProductAttributeDefinitions();
        this.buildDynamicProductAttributes();
        await this.initForm();
      }
    });

    this.session.currentCompanyData$.subscribe(company => {
      this.hasLimitedEditionEnabled = company?.hasLimitedEdition ?? false;
      this.canViewPrice = company?.hasPrice ?? false;
       this.hasItemsWithoutSerialNumberEnabled = company?.hasItemsWithoutSerialNumber ?? false;
    });

    this.productForm.get('isLimitedEdition')?.valueChanges.subscribe(val => {
      const limitedEditionSizeControl = this.productForm.get('limitedEditionSize');

      if (val === 'true') {
        limitedEditionSizeControl?.enable();
        limitedEditionSizeControl?.setValidators([Validators.required, Validators.min(1)]);
      } else {
        limitedEditionSizeControl?.disable();
        limitedEditionSizeControl?.clearValidators();
      }

      limitedEditionSizeControl?.updateValueAndValidity();
    });

    this.hasCustomisation = await this.permissionService.hasFeaturePermissions(Features.ProductCustomisations);
  }


  onLazyLoadBrands(event: any): void {
    this.getBrands(event);
  }

  async getBrands(event: any): Promise<void> {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);

    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.brandsRowsPerPageOption,
      additionalFilters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    if (search) {
      payload.textFilter = search;
    }

    const brandsPage = await firstValueFrom(this.brandsService.apiBrandsFilterComboPost(payload));

    this.brandsList = brandsPage.results ?? [];
    this.brandsTotalRecords = brandsPage.totalCount ?? 0;
  }

  private autoSelectBrandWhenOnlyOneIsAvailable(): void {
    if (this.product.brandId != null) {
      return;
    }

    if (this.brandsTotalRecords !== 1) {
      return;
    }

    const brandId = this.brandsList[0]?.id;

    if (brandId == null) {
      return;
    }

    this.productForm.get('brandId')?.setValue([brandId]);
  }

  onLazyLoadProductTypes(event: any) {
    this.getProductTypes(event);
  }

  async getProductTypes(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.productTypeRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const productTypesPage = await firstValueFrom(this.productTypesService.apiProductTypesFilterComboPost(payload));
    this.productTypes = productTypesPage.results ?? [];
    this.productTypeTotalRecords = productTypesPage.totalCount ?? 0;
  }

  onLazyLoadCollections(event: any) {
    this.getCollections(event);
  }

  async getCollections(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.collectionsRowsPerPageOption
    }

    if (search) {
      payload.textFilter = search;
    }

    const collectionsPage = await firstValueFrom(this.collectionsService.apiCollectionsFilterComboPost(payload));
    this.collectionsList = collectionsPage.results ?? [];
    this.collectionsTotalRecords = collectionsPage.totalCount ?? 0;
  }

  onLazyLoadCategories(event: any) {
    this.getCategories(event);
  }

  async getCategories(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.categoriesRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const categoriesPage = await firstValueFrom(this.categoriesService.apiCategoriesFilterComboPost(payload));
    this.categoriesList = categoriesPage.results ?? [];
    this.categoriesTotalRecords = categoriesPage.totalCount ?? 0;
  }

  isLimitedEditionSizeDisabled(): boolean {
    return this.productForm.get('isLimitedEdition')?.value === 'false';
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  private async loadProductAttributeDefinitions(): Promise<void> {
    if (!this.productAttributesFeatureEnabled) {
      this.productAttributeDefinitions = [];
      this.productAttributesArray.clear();
      return;
    }

    const filter: FilterRequest = {
      pageSize: 500,
      pageNumber: 1,
      filters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    this.productAttributeDefinitions =
      (await firstValueFrom(
        this.productAttributeDefinitionsService.apiProductAttributeDefinitionsFilterPost(filter)
      )).results ?? [];
  }

  private buildDynamicProductAttributes(): void {
    this.productAttributesArray.clear();

    // Build a dynamic form group per definition
    this.productAttributeDefinitions.forEach(definition => {
      this.productAttributesArray.push(this.createProductAttributeGroup(definition));
    });
  }

  private createProductAttributeGroup(definition: ProductAttributeDefinitionResponse): FormGroup {
    const isFixedValues = definition.productAttributeDataType === AttributeDataType.FixedValues;
    const isMultiNumeric = !isFixedValues && definition.allowMultipleValues;

    return this.fb.group({
      definitionId: [definition.id],
      code: [definition.definitionCode],
      productAttributeDataType: [definition.productAttributeDataType],
      allowMultipleValues: [definition.allowMultipleValues],
      value: isFixedValues ? this.fb.control([]) : this.createTextboxValueControl(definition.productAttributeDataType),
      values: this.fb.array(isMultiNumeric ? [this.createTextboxValueControl(definition.productAttributeDataType)] : [])
    });
  }

  private createTextboxValueControl(dataType: AttributeDataType) {
    if (dataType === AttributeDataType.FixedValues) {
      return;
    }

    const validators = []; // [Validators.required]

    if (dataType === AttributeDataType.Integer) {
      validators.push(Validators.pattern(/^-?\d+$/));
    }

    if (dataType === AttributeDataType.Decimal) {
      validators.push(Validators.pattern(/^-?\d+([.,]\d+)?$/));
    }

    return this.fb.control(null, validators);
  }

  getNumericValuesArray(attributeIndex: number): FormArray {
    return this.productAttributesArray.at(attributeIndex).get('values') as FormArray;
  }

  addNumericMultiValue(attributeIndex: number): void {

    const maxDynamicFields = 10;

    const attributeGroup = this.productAttributesArray.at(attributeIndex) as FormGroup;
    const valuesArray = this.getNumericValuesArray(attributeIndex);
    const dataType = attributeGroup.get('productAttributeDataType')?.value;

    if (valuesArray.length >= maxDynamicFields) {
      return;
    }

    valuesArray.push(this.createTextboxValueControl(dataType));
  }

  removeNumericMultiValue(attributeIndex: number, valueIndex: number): void {
    const valuesArray = this.getNumericValuesArray(attributeIndex);

    if (valuesArray.length <= 1) {
      return;
    }

    valuesArray.removeAt(valueIndex);
  }

  isFixedValuesAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.FixedValues;
  }

  isTextAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Text;
  }

  isIntegerAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Integer;
  }

  isDecimalAttribute(attribute: AbstractControl | null): boolean {
    return attribute?.get('productAttributeDataType')?.value === AttributeDataType.Decimal;
  }

  allowMultipleValuesAttribute(attribute: AbstractControl | null): boolean {
    return !!attribute?.get('allowMultipleValues')?.value;
  }

  getAttributeLabel(definition: ProductAttributeDefinitionResponse): string {
    // Use translation for current language
    const currentLang = this.session?.locale || 'en';
    const translations = definition?.productAttributeDefinitionTranslations || [];

    const currentTranslation = translations.find((item: ProductAttributeDefinitionTranslationResponse) => item.isoCode === currentLang);

    return currentTranslation?.translatedName || definition?.definitionCode || '';
  }

  getProductAttributeDropdownOptions(definition: ProductAttributeDefinitionResponse): Array<{ id: string; text: string }> {
    const currentLang = this.session?.locale || 'en';
    const existingValues = this.product?.productAttributeValues ?? [];

    const values = definition?.productAttributeValues?.filter(x => x.isActive || existingValues.some(y => y.id === x.id)) || [];

    // response values to dropdown options
    return values.map((item: any) => {
      const translations = item.productAttributeValueTranslations || [];
      const currentTranslation = translations.find((translation: ProductAttributeValueTranslationResponse) => translation.isoCode === currentLang);

      return {
        id: item.id || 0,
        text: currentTranslation?.translatedValue || item.valueCode
      };
    });
  }

    private patchDynamicProductAttributes(): void {
       const existingValues = this.product?.productAttributeValues ?? [];

       this.productAttributesArray.controls.forEach(control => {
         const group = control as FormGroup;
         const definitionId = group.get('definitionId')?.value;
         const dataType = group.get('productAttributeDataType')?.value;
         const allowMultipleValues = !!group.get('allowMultipleValues')?.value;

         const matchingValues = existingValues.filter((item: any) => item.productAttributeDefinition.id === definitionId);

         if (dataType === AttributeDataType.FixedValues) {
           const selectedValues = matchingValues
             .map((item: any) => item.productAttributeValueId ?? item.id)
             .filter((value: any) => value !== null && value !== undefined);

           group.get('value')?.setValue(allowMultipleValues ? selectedValues : selectedValues.slice(0, 1));

           return;
         }

         const mappedValues = matchingValues
           .map((item: any) => item.value ?? item.valueCode)
           .filter((value: any) => value !== null && value !== undefined && value !== '');

         if (allowMultipleValues) {
           const valuesArray = group.get('values') as FormArray;

           valuesArray.clear();

           if (mappedValues.length) {
             mappedValues.forEach((value: any) => {
               const controlToAdd = this.createTextboxValueControl(dataType);

               controlToAdd?.setValue(value);
               valuesArray.push(controlToAdd);
             });
           } else {
             valuesArray.push(this.createTextboxValueControl(dataType));
           }

           return;
         }

         group.get('value')?.setValue(mappedValues[0] ?? null);
       });
     }

  async initForm() {
    this.productForm.patchValue({
      name: this.product.name,
      sku: this.product.sku,
      price: this.product.price,
      description: this.product.description,
      isSerialNumberRequired: this.product.isSerialNumberRequired ? 'true' : 'false',
      imageUrl: this.product.imageUrl,
      comment: this.product.comment,
      warrantyDurationMonths: this.product.warrantyDurationMonths,
      isCustomisable: this.product.isCustomisable ? 'true' : 'false',
      isLimitedEdition: this.product.isLimitedEdition ? 'true' : 'false',
      limitedEditionSize: this.product.limitedEditionSize ?? null,
      //generateLimitedEditionSerialNumbers: !!this.product.generateLimitedEditionSerialNumbers,
    });

    // load dropdowns
    await Promise.all([
      this.getProductTypes({ pageIndex: 1, pageSize: this.productTypeRowsPerPageOption, search: '' }),
      this.getCollections({ pageIndex: 1, pageSize: this.collectionsRowsPerPageOption, search: '' }),
      this.getCategories({ pageIndex: 1, pageSize: this.categoriesRowsPerPageOption, search: '' }),
      this.getBrands({ pageIndex: 1, pageSize: this.brandsRowsPerPageOption, search: '' })
    ]);

    if (this.productAttributesFeatureEnabled) {
          this.patchDynamicProductAttributes();
    }
    // ensure selected ProductType exists in productTypes list
    const fetchedProductType = this.product.productType ?? null;
    if (fetchedProductType?.id) {
      const exists = (this.productTypes ?? []).some((pt: any) => pt.id === fetchedProductType.id);
      if (!exists) {
        const current = await firstValueFrom(this.productTypesService.apiProductTypesComboValueIdGet(fetchedProductType.id))
        this.productTypes = [current, ...(this.productTypes ?? [])];
      }
    }

    const onlyActiveBrandId = this.brandsList[0]?.id;

    this.hasOnlyOneActiveBrand = this.brandsTotalRecords === 1 && (this.product.brandId == null || this.product.brandId === onlyActiveBrandId);

    // ensure the currently assigned brand is available
    const currentBrandId = this.product.brandId;
    if (currentBrandId != null) {
      const currentBrandExists = this.brandsList.some(brand => brand.id === currentBrandId);
      if (!currentBrandExists) {
        const currentBrand = await firstValueFrom(
          this.brandsService.apiBrandsComboValueIdGet(currentBrandId)
        );

        this.brandsList = [
          currentBrand,
          ...this.brandsList
        ];
      }
      this.productForm.get('brandId')?.setValue([currentBrandId]);
    }

    this.autoSelectBrandWhenOnlyOneIsAvailable();

    // ensure selected Categories exist in categoriesList
    const fetchedCategories = this.product.categories ?? [];
    if (fetchedCategories.length > 0) {
      for (const cat of fetchedCategories) {
        const exists = (this.categoriesList ?? []).some((c: any) => c.id === cat.id);
        if (!exists) {
          const current = await firstValueFrom(this.categoriesService.apiCategoriesComboValueIdGet(cat.id!))
          this.categoriesList = [current, ...(this.categoriesList ?? [])];
        }
      }
    }

    // ensure selected Collections exist in collectionsList
    const fetchedCollections = this.product.collections ?? [];
    if (fetchedCollections.length > 0) {
      for (const col of fetchedCollections) {
        const exists = (this.collectionsList ?? []).some((c: any) => c.id === col.id);
        if (!exists) {
          const current = await firstValueFrom(this.collectionsService.apiCollectionsComboValueIdGet(col.id!))
          this.collectionsList = [current, ...(this.collectionsList ?? [])];
        }
      }
    }

    // bind selected vals
    const selectedProductType = fetchedProductType?.id ? [fetchedProductType.id] : [];
    const selectedCategories = fetchedCategories.map((c: any) => c.id);
    const selectedCollections = fetchedCollections.map((c: any) => c.id);

    this.productForm.patchValue({
      productType: selectedProductType,
      categories: selectedCategories,
      collections: selectedCollections
    });
  }

  async submitForm() {
    if (this.productForm.valid) {
      const collectionIds = this.productForm.controls['collections'].value || [];
      const categoryIds = this.productForm.controls['categories'].value || [];

      let dynamicProductAttributes: any[] = [];

      if (this.productAttributesFeatureEnabled) {
        dynamicProductAttributes = this.productAttributesArray.controls
          .map(productAttributeControl => {
            const productAttributeForm = productAttributeControl as FormGroup;

            const attributeDefinitionId = productAttributeForm.get('definitionId')?.value;
            const productAttributeDataType = productAttributeForm.get('productAttributeDataType')?.value;
            const productAttributeAllowsMultipleValues = !!productAttributeForm.get('allowMultipleValues')?.value;

            let productAttributeValues: any[];

            if (productAttributeDataType === AttributeDataType.FixedValues) {
              const selectedDropdownValues = productAttributeForm.get('value')?.value || [];

              productAttributeValues = Array.isArray(selectedDropdownValues)
                ? selectedDropdownValues
                : [selectedDropdownValues];
            } else if (productAttributeAllowsMultipleValues) {
              productAttributeValues = (productAttributeForm.get('values') as FormArray).value;
            } else {
              const singleInputValue = productAttributeForm.get('value')?.value;

              productAttributeValues = [singleInputValue];
            }

            const filledProductAttributeValues = productAttributeValues.filter(value =>
              value !== null &&
              value !== undefined &&
              value !== ''
            );

            return {
              attributeDefinitionId,
              values: filledProductAttributeValues
            };
          })
          .filter(productAttribute => {
            if (!productAttribute.values) {
              return false;
            }

            return productAttribute.values.length > 0;
          });
      }

      const payload: ProductRequest = {
        brandId: this.productForm.controls['brandId'].value?.[0] ?? null,
        name: this.productForm.controls['name'].value,
        sku: this.productForm.controls['sku'].value,
        productTypeId: this.productForm.controls['productType'].value?.[0],
        collectionIds: collectionIds,
        categoryIds: categoryIds,
        price: this.productForm.controls['price'].value,
        description: this.productForm.controls['description'].value,
        warrantyDurationMonths: this.productForm.controls['warrantyDurationMonths'].value,
        imageUrl: this.productForm.controls['imageUrl'].value,
        comment: this.productForm.controls['comment'].value,
        isCustomisable: this.productForm.controls['isCustomisable'].value === 'true',
        isSerialNumberRequired: this.productForm.controls['isSerialNumberRequired'].value === 'true',
        isLimitedEdition: this.productForm.controls['isLimitedEdition'].value === 'true',
        limitedEditionSize: this.productForm.controls['limitedEditionSize'].value,
        generateLimitedEditionSerialNumbers: this.productForm.controls['generateLimitedEditionSerialNumbers'].value,
        productAttributes: dynamicProductAttributes
      };

      await firstValueFrom(this.productsService.apiProductsUpdateIdPut(this.productId, payload));

      this.router.navigate(['/products', this.productId]);
    }
  }
}
